import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
    afterRenderEffect,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    untracked,
    viewChildren,
} from '@angular/core';
import { DocumentItemPartial, FavoriteFieldEntity } from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import {
    CALLOUT_COLORS,
    resolveCalloutColor,
} from '../../../../../callout-colors';
import { StatsGridComponent } from '../../../../../components/stats-grid/stats-grid.component';
import {
    DocumentStatEntry,
    parseStatsEntries,
    resolveStatsColumns,
    resolveStatsStyle,
    sameStatsEntries,
    STATS_COLUMNS,
    STATS_LIMITS,
    STATS_STYLES,
    StatsStyleKey,
} from '../../../../../stats-options';
import { CustomColorSwatchComponent } from '../../custom-color-swatch/custom-color-swatch.component';
import { RaEditTooltipDirective } from '../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { CardBaseComponent } from '../_card-base/card-base.component';
import { RaLookPanelDirective } from '../_card-base/look-panel.directive';
import { CardTypes } from '../_card-base/enums/card-types.enum';

/** Menu "Aparência" do rail (cor, estilo e colunas do PDF). */

/**
 * Card da GRADE DE INDICADORES (item STATS), proposta A do protótipo: o card é a
 * própria grade como ela imprime, e cada card edita no lugar (número em cima, legenda
 * embaixo). No hover aparecem o "x" e a alça para reordenar; o card tracejado ocupa a
 * próxima vaga para adicionar. Cor, colunas e estilo moram no rail flutuante.
 *
 * Salva a lista INTEIRA (update-stats) ao sair de um campo alterado, ao remover, ao
 * adicionar e ao reordenar. Enquanto um campo da grade tem foco, o eco do servidor não
 * sobrescreve o que está sendo digitado.
 */
import { UndoToastService } from '../_card-base/undo-toast.service';
@Component({
    selector: 'stats-card',
    templateUrl: 'stats-card.component.html',
    styleUrl: 'stats-card.component.scss',
    imports: [
        CardBaseComponent,
        CustomColorSwatchComponent,
        StatsGridComponent,
        RaIconComponent,
        RaEditTooltipDirective,
        RaLookPanelDirective,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        TranslatePipe,
    ],
})
export class StatsCardComponent {
    public field = input<DocumentItemPartial>();
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);

    public readonly cardTypes = CardTypes;
    public readonly colorOptions = CALLOUT_COLORS;
    public readonly columnOptions = STATS_COLUMNS;
    public readonly styleOptions = STATS_STYLES;
    public readonly limits = STATS_LIMITS;

    // * [Injects]
    public editService = inject(EditService);
    public editDataService = inject(EditDataService);
    private _editItemsService = inject(EditItemsService);
    private _undoToast = inject(UndoToastService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);

    /** Cards em edição (cópia local; o servidor recebe no commit). */
    public entries = signal<DocumentStatEntry[]>([]);

    /** Algum campo da grade tem foco: o eco do servidor espera. */
    private _focused = signal(false);

    private _labelAreas = viewChildren<ElementRef<HTMLTextAreaElement>>('labelArea');

    public currentField = computed(() => this.getCurrentField());
    /** Cor livre sendo arrastada no seletor do menu: o card mostra na hora, sem salvar. */
    public colorPreview = signal<string | null>(null);
    public color = computed(() =>
        resolveCalloutColor(this.colorPreview() ?? this.currentField()?.configs?.calloutColor)
    );
    public columns = computed(() =>
        resolveStatsColumns(this.currentField()?.configs?.statsColumns)
    );
    public styleKey = computed(() => resolveStatsStyle(this.currentField()?.configs?.statsStyle));
    public canAdd = computed(() => this.entries().length < STATS_LIMITS.MAX_ENTRIES);

    public blocked = computed(
        () =>
            this.editService.loading() ||
            this.editDataService.currentSessionIsDisabled() ||
            this.editDataService.documentIsBlocked()
    );

    constructor() {
        // Servidor (ou realtime) mudou a lista: adota, a não ser que o usuário esteja
        // digitando num dos cards agora.
        effect(() => {
            const serverEntries = parseStatsEntries(this.currentField()?.value);
            if (untracked(this._focused)) return;
            untracked(() => {
                if (!sameStatsEntries(serverEntries, this.entries()))
                    this.entries.set(serverEntries);
            });
        });

        // A legenda cresce com o texto (sem barra de rolagem dentro do card).
        afterRenderEffect(() => {
            this.entries();
            for (const ref of this._labelAreas()) this.autosize(ref.nativeElement);
        });
    }

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }

    // #region * [Edição dos cards]

    public onValueInput(index: number, event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.entries.update(list => list.map((e, i) => (i === index ? { ...e, value } : e)));
    }

    public onLabelInput(index: number, event: Event): void {
        const target = event.target as HTMLTextAreaElement;
        const label = target.value.replace(/\n/g, ' ');
        this.autosize(target);
        this.entries.update(list => list.map((e, i) => (i === index ? { ...e, label } : e)));
    }

    /** Enter confirma o campo (a legenda não tem quebra de linha): tira o foco e salva. */
    public blurOnEnter(event: KeyboardEvent): void {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        (event.target as HTMLElement | null)?.blur();
    }

    public onFocusIn(): void {
        this._focused.set(true);
    }

    /** Foco saiu da grade inteira (não só de um campo para outro): libera o eco e salva. */
    public onFocusOut(event: FocusEvent): void {
        const next = event.relatedTarget as Node | null;
        if (next && this._host.nativeElement.contains(next)) return;

        this._focused.set(false);
        this.commit();
    }

    /** Remove na hora; o toast oferece Desfazer, que reinsere o indicador onde estava. */
    public removeEntry(index: number): void {
        if (this.blocked()) return;
        const removed = this.entries()[index];
        if (removed === undefined) return;

        this.entries.update(list => list.filter((_, i) => i !== index));
        this.commit();

        this._undoToast.show('document.edit.statsCard.entryRemoved', () => {
            if (this.blocked()) return;
            this.entries.update(list => {
                const next = [...list];
                next.splice(Math.min(index, next.length), 0, removed);
                return next;
            });
            this.commit();
        });
    }

    public addEntry(): void {
        if (this.blocked() || !this.canAdd()) return;

        this.entries.update(list => [...list, { value: '', label: '' }]);
        this.commit();

        // Foca o número do card novo assim que ele existir no DOM.
        const index = this.entries().length - 1;
        setTimeout(() => this.focusValue(index));
    }

    public drop(event: CdkDragDrop<DocumentStatEntry[]>): void {
        if (event.previousIndex === event.currentIndex) return;

        const list = [...this.entries()];
        moveItemInArray(list, event.previousIndex, event.currentIndex);
        this.entries.set(list);
        this.commit();
    }

    /** Grava a lista se ela mudou em relação ao que o campo tem. */
    public commit(): void {
        const field = this.currentField();
        if (!field?._id || this.type() !== CardTypes.DEFAULT || this.blocked()) return;

        const next = this.entries().map(e => ({
            value: e.value.trim().slice(0, STATS_LIMITS.MAX_VALUE_LENGTH),
            label: e.label.trim().slice(0, STATS_LIMITS.MAX_LABEL_LENGTH),
        }));
        if (sameStatsEntries(next, parseStatsEntries(field.value))) return;

        this.entries.set(next);
        this._editItemsService.updateStats(field._id, next);
    }

    public focusValue(index: number): void {
        const inputs =
            this._host.nativeElement.querySelectorAll<HTMLInputElement>('input.stat-value');
        inputs[index]?.focus();
    }

    public autosize(textarea: HTMLTextAreaElement): void {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }

    // #endregion

    // #region * [Painel de design: cor, colunas, estilo]

    /** Uma vaga por coluna, para desenhar o glifo de colunas do chip e do menu. */
    public columnSlots(count: number): number[] {
        return Array.from({ length: count }, (_, i) => i);
    }

    /** Família (`blue`...) ou cor livre (`#rrggbb`). */
    public selectColor(color: string): void {
        this._saveConfig({ calloutColor: color });
    }

    /** O seletor livre fechou: salva o #rrggbb (otimista, como as famílias) e larga a prévia. */
    public chooseCustomColor(hex: string): void {
        this.colorPreview.set(null);
        this.selectColor(hex);
    }

    public selectColumns(columns: number): void {
        this._saveConfig({ statsColumns: columns });
    }

    public selectStyle(style: StatsStyleKey): void {
        this._saveConfig({ statsStyle: style });
    }

    private _saveConfig(change: {
        calloutColor?: string;
        statsColumns?: number;
        statsStyle?: string;
    }): void {
        // O menu fica aberto: cor, estilo e colunas se ajustam juntos, vendo a grade mudar.
        const field = this.currentField();
        if (!field?._id) return;

        this._editItemsService.updateItemConfig(field._id, { ...(field.configs ?? {}), ...change });
    }

    // #endregion
}
